/**
 * Subscription: the Access membership, and switching it.
 *
 * The tier catalogue is the server's, from GET /api/access/tiers. The current
 * tier is `brand.accessTier`, falling back to `brand.plan` for brands that
 * predate the Access tiers - same order the live page uses.
 *
 * Switching is a real billing change, so it asks first. The live page used
 * window.confirm; this uses the pay-sheet-shaped confirmation the rest of the
 * app uses, because a native dialog in the middle of a designed screen is the
 * one place the product stops looking like itself.
 *
 * @module web/screens/app/SubscriptionScreen
 */
import { useEffect, useState } from "react";
import { ApiError, get, post } from "../../api/client.ts";
import { fmtEur } from "../../money.ts";
import { useBrand } from "../../state/BrandContext.tsx";
/** One entry of GET /api/access/tiers. Lived in the signup wizard until that stopped showing tiers. */
interface AccessTierCard {
  id: string;
  name: string;
  priceCentsMonthly: number;
  brands: number;
  includes: string[];
}

/**
 * Which tiers sit above which, for LABELLING only.
 *
 * The server owns this decision and answers 402 whatever the client believes
 * (pricing-routes.ts, tierRank). This copy exists so the screen does not offer
 * a "Switch" button that can only ever fail - a control must say what it does.
 */
const RANK: Record<string, number> = { ondemand: 1, black: 2 };
const rankOf = (t: string | null | undefined): number => (t ? RANK[t] ?? 0 : 0);

export function SubscriptionScreen() {
  const { brand, refresh, notify } = useBrand();
  const [tiers, setTiers] = useState<AccessTierCard[]>([]);
  const [failed, setFailed] = useState(false);
  const [pending, setPending] = useState<AccessTierCard | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let live = true;
    void get<{ tiers: AccessTierCard[] }>("/api/access/tiers")
      .then(r => { if (live) setTiers(r.tiers ?? []); })
      .catch(() => { if (live) setFailed(true); });
    return () => { live = false; };
  }, []);

  const currentId = brand?.accessTier ?? brand?.plan ?? null;
  const current = tiers.find(t => t.id === currentId) ?? null;

  async function switchTo(tier: AccessTierCard): Promise<void> {
    setBusy(true);
    try {
      await post("/api/access/select-tier", { tier: tier.id });
      await refresh();
      notify(`Switched to ${tier.name}.`, "success");
    } catch (e) {
      // 402 is not a failure, it is the answer: this one is bought, not picked.
      const paywalled = e instanceof ApiError && e.status === 402;
      notify(
        e instanceof Error ? e.message : "Could not switch tier.",
        paywalled ? "" : "error",
      );
    }
    setBusy(false);
    setPending(null);
  }

  return (
    <>
      <header className="page-head fade-in">
        <h1 className="t-h2">Subscription</h1>
        <p className="t-body" style={{ marginTop: 4 }}>
          Membership sets your rate. Without one you pay list price on every
          brief.
        </p>
      </header>

      <div className="card fade-in" style={{ marginTop: 28, maxWidth: 640 }}>
        <div className="t-eyebrow">Currently on</div>
        <h2 className="tier-card-name" style={{ fontSize: 32 }}>{current ? current.name : "No tier selected"}</h2>
        <div className="t-body">
          {current
            ? `${fmtEur(current.priceCentsMonthly)}/month${current.brands ? ` · up to ${current.brands} brand${current.brands === 1 ? "" : "s"}` : ""}`
            : "Pick a tier below to get started."}
        </div>
        <div style={{ display: "flex", gap: 10, marginTop: 20, flexWrap: "wrap" }}>
          <button className="btn btn-ghost" onClick={() => void openPortal(notify)}>
            Manage billing (Stripe portal)
          </button>
        </div>
      </div>

      <div className="rule" style={{ margin: "40px 0 18px" }}>Switch tier</div>
      {failed || tiers.length === 0 ? (
        <div className="t-body-sm" style={{ color: "var(--ink-3)" }}>Tier catalog unavailable.</div>
      ) : (
        <div className="tier-grid">
          {tiers.map(t => {
            const isCurrent = t.id === currentId;
            const featured = t.id === "black";
            return (
              <article className={`tier-card${featured ? " featured" : ""}`} key={t.id} data-tier={t.id}>
                <div className="t-eyebrow">{featured ? "Recommended" : "Flexible"}</div>
                <h2 className="tier-card-name">{t.name}</h2>
                <div className="tier-card-price">{fmtEur(t.priceCentsMonthly)}<span>/mo</span></div>
                <ul className="tier-card-list">{(t.includes ?? []).map(i => <li key={i}>{i}</li>)}</ul>
                <button
                  className={`btn ${isCurrent || !featured ? "btn-ghost" : "btn-brand"} tier-switch-btn`}
                  data-tier={t.id}
                  disabled={isCurrent || busy}
                  onClick={() => {
                    // Moving up is a purchase and there is no payment path yet.
                    // Opening a "Switch tier" sheet that ends in 402 would be a
                    // control that lies about what it does.
                    if (rankOf(t.id) > rankOf(currentId)) {
                      notify(`${t.name} is set up with you - we'll take it from here.`, "");
                      return;
                    }
                    setPending(t);
                  }}
                >
                  {isCurrent ? "Current" : rankOf(t.id) > rankOf(currentId) ? "Talk to us" : "Switch"}
                </button>
              </article>
            );
          })}
        </div>
      )}

      {pending && (
        <>
          <div className="pay-sheet-overlay open" onClick={() => setPending(null)} />
          <div className="pay-sheet open" data-state="ask" role="dialog" aria-modal="true">
            <div className="pay-grip" />
            <div className="pay-body">
              <div className="pay-eyebrow">Switch tier</div>
              <h2 className="pay-title">{pending.name}</h2>
              <div className="pay-row">
                <span className="pay-row-k">New rate</span>
                <span className="pay-amount">{fmtEur(pending.priceCentsMonthly)}</span>
              </div>
              <button className="pay-cta" disabled={busy} onClick={() => void switchTo(pending)}>
                {busy ? "Switching…" : "Switch tier"}
              </button>
              <button className="pay-cancel" onClick={() => setPending(null)}>Cancel</button>
              <p className="pay-note">Your new rate takes effect at the next billing cycle.</p>
            </div>
          </div>
        </>
      )}
    </>
  );
}

async function openPortal(notify: (m: string, tone?: "" | "error" | "success") => void): Promise<void> {
  try {
    const { url } = await post<{ url?: string }>("/api/stripe/portal");
    if (url) { window.location.href = url; return; }
    notify("Billing portal is not available right now.", "error");
  } catch (e) {
    notify(e instanceof Error ? e.message : "Could not open the billing portal.", "error");
  }
}
